import { useState } from 'react';
import { estadoDe } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { ConfirmDialog } from '@/ui/Dialog';
import { Field, Input, Select } from '@/ui/Field';
import { Skeleton } from '@/ui/Skeleton';
import { BarraDeProgreso } from '@/components/BarraDeProgreso';
import { BuscadorDePersonas, type PersonaDelDirectorio } from '@/components/BuscadorDePersonas';
import { EstadoError } from '@/components/EstadoError';
import {
  useAreasDeGestion, useAsignaciones, useBorrarAsignacion, useCrearAsignacion,
  useProgresoDelEquipo, useRecordarAsignacion, useReiniciarProgreso,
  type Asignacion, type CuerpoDeAsignacion, type CursoDetalle, type DestinoDeAsignacion,
} from '../api';

/**
 * Asignaciones — dirigir el curso a un grupo con fecha y obligatoriedad, y ver
 * cómo va el equipo.
 *
 * ## Un fallo al cargar parecía «no hay ninguna»
 *
 * La lista se pedía con un `catch {}` vacío y, si fallaba, pintaba «Sin
 * asignaciones formales todavía»: **un error indistinguible de un curso sin
 * asignar**. Lo mismo con las áreas —el desplegable salía vacío sin decir
 * por qué— y con el progreso del equipo, que al fallar volvía a la lista
 * sacando un toast. Los tres dicen ahora qué pasó y dejan reintentar.
 *
 * ## «Por rol» ofrece tres roles, y la base tiene cuatro
 *
 * Colaborador, coordinador y administrador están escritos aquí; **«Auditor» no
 * se puede elegir**. La lista real sale de `GET /api/admin/roles`, que es solo de
 * administración, y quien gestiona un curso con `cursos:manage` no puede leerla.
 * Arreglarlo es una decisión de contrato —un endpoint de lectura para quien
 * asigna— y queda escrito en la ficha, no resuelto aquí a escondidas.
 *
 * ## «Reiniciar el progreso» se ofrece a quien no puede hacerlo
 *
 * Todo el panel exige `cursos:manage`, menos `POST /admin/courses/{id}/reset/{uid}`,
 * que es `AdminOnly`: un coordinador ve el botón y recibe un 403. Ocultarlo
 * pediría saber si la sesión es de administración, y la sesión no lo trae
 * —solo `is_staff`— y comparar el nombre del rol está prohibido. Así que el
 * botón sigue donde estaba, el 403 se explica, y la decisión —abrir el reinicio
 * a `cursos:manage` o llevar `is_admin` a la sesión— queda en la ficha.
 */

const DESTINOS: Array<[DestinoDeAsignacion, string]> = [
  ['todos', 'Todos'], ['area', 'Por área'], ['rol', 'Por rol'], ['cargo', 'Por cargo'], ['usuario', 'Por persona'],
];

const ROLES: Array<[string, string]> = [
  ['colaborador', 'Colaboradores'], ['coordinador', 'Coordinadores'], ['admin', 'Administradores'],
];

export function Asignaciones({ curso }: { curso: CursoDetalle }) {
  const [viendoEquipo, setViendoEquipo] = useState(false);
  return viendoEquipo
    ? <ProgresoDelEquipo curso={curso} onVolver={() => setViendoEquipo(false)} />
    : <Lista curso={curso} onVerEquipo={() => setViendoEquipo(true)} />;
}

/* ── Crear y listar ────────────────────────────────────────────────────── */

function Lista({ curso, onVerEquipo }: { curso: CursoDetalle; onVerEquipo: () => void }) {
  const asignaciones = useAsignaciones(curso.id, true);
  const borrar = useBorrarAsignacion();
  const recordar = useRecordarAsignacion();
  const [borrando, setBorrando] = useState<Asignacion | null>(null);

  return (
    <div className="flex flex-col gap-5">
      <p className="rounded-sm border border-line bg-sunken p-3 text-2 text-ink-2">
        <i className="bi bi-info-circle mr-1.5 text-brand-ink" aria-hidden="true" />
        Una <strong>asignación</strong> dirige el curso a un grupo, con fecha límite y carácter
        obligatorio. Sale como pendiente en «Mis cursos» de cada persona y permite seguir al equipo.
      </p>

      <NuevaAsignacion curso={curso} />

      <section aria-labelledby="asg-activas" className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 id="asg-activas" className="text-3 font-bold text-brand-ink">
            <i className="bi bi-list-task" aria-hidden="true" /> Asignaciones activas
          </h3>
          <Button variant="secondary" size="sm" onClick={onVerEquipo}>
            <i className="bi bi-people" aria-hidden="true" /> Progreso del equipo
          </Button>
        </div>

        {asignaciones.isLoading ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        ) : asignaciones.isError ? (
          <EstadoError
            err={asignaciones.error}
            que="las asignaciones"
            onReintentar={() => void asignaciones.refetch()}
          />
        ) : !asignaciones.data?.length ? (
          <p className="rounded-sm border border-line bg-sunken p-3 text-center text-3 text-muted">
            Este curso todavía no tiene asignaciones.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {asignaciones.data.map((a) => (
              <FilaDeAsignacion
                key={a.id}
                a={a}
                recordando={recordar.isPending && recordar.variables?.asignacionId === a.id}
                onRecordar={() =>
                  recordar.mutate(
                    { cursoId: curso.id, asignacionId: a.id },
                    {
                      onSuccess: (r) => Utils.toast(r.message || 'Recordatorio enviado', 'success'),
                      onError: (err) =>
                        Utils.toast(err instanceof Error ? err.message : 'No se pudo enviar', 'error'),
                    },
                  )
                }
                onBorrar={() => setBorrando(a)}
              />
            ))}
          </ul>
        )}
      </section>

      <ConfirmDialog
        abierto={borrando != null}
        onCambio={(v) => { if (!v) setBorrando(null); }}
        titulo="Eliminar la asignación"
        descripcion={
          borrando
            ? `«${borrando.target_label}» deja de ver el curso como pendiente. Su avance no se pierde.`
            : ''
        }
        textoConfirmar="Eliminar"
        destructivo
        cargando={borrar.isPending}
        onConfirmar={() => {
          if (!borrando) return;
          borrar.mutate(
            { cursoId: curso.id, asignacionId: borrando.id },
            {
              onSuccess: () => { setBorrando(null); Utils.toast('Asignación eliminada', 'info'); },
              onError: (err) => {
                setBorrando(null);
                Utils.toast(err instanceof Error ? err.message : 'No se pudo eliminar', 'error');
              },
            },
          );
        }}
      />
    </div>
  );
}

function FilaDeAsignacion({
  a, recordando, onRecordar, onBorrar,
}: {
  a: Asignacion;
  recordando: boolean;
  onRecordar: () => void;
  onBorrar: () => void;
}) {
  // La fecha LOCAL. `toISOString()` da la de UTC, y en Bogotá —UTC-5— desde las
  // siete de la tarde ya es mañana: una asignación que vence hoy salía
  // «vencida» cinco horas antes de tiempo. El vanilla lo hacía así.
  const d = new Date();
  const hoy = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const vencida = !!a.due_date && a.due_date < hoy;
  return (
    <li className="flex items-center gap-3 rounded-sm border border-line bg-surface p-3">
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-1.5 text-3 font-semibold text-ink">
          {a.target_label}
          <span className="font-normal text-muted">
            · {a.target_count} persona{a.target_count === 1 ? '' : 's'}
          </span>
          {a.is_mandatory && <Badge tone="danger">Obligatorio</Badge>}
        </p>
        <p className={cn('text-2', vencida ? 'text-danger-ink' : 'text-muted')}>
          <i className={cn('bi', a.due_date ? 'bi-calendar-event' : 'bi-infinity')} aria-hidden="true" />{' '}
          {a.due_date ? `Vence el ${Utils.fmtDateOnly(a.due_date)}${vencida ? ' (vencida)' : ''}` : 'Sin fecha límite'}
          {a.note && <span className="text-muted"> · {a.note}</span>}
        </p>
      </div>
      <Button
        variant="ghost"
        size="sm"
        loading={recordando}
        aria-label={`Enviar un recordatorio a ${a.target_label}`}
        onClick={onRecordar}
      >
        <i className="bi bi-bell" aria-hidden="true" />
      </Button>
      <Button variant="ghost" size="sm" aria-label={`Eliminar la asignación a ${a.target_label}`} onClick={onBorrar}>
        <i className="bi bi-trash text-danger-ink" aria-hidden="true" />
      </Button>
    </li>
  );
}

function NuevaAsignacion({ curso }: { curso: CursoDetalle }) {
  const crear = useCrearAsignacion();
  const [destino, setDestino] = useState<DestinoDeAsignacion>('todos');
  const areas = useAreasDeGestion(destino === 'area');
  const [areaId, setAreaId] = useState('');
  const [rol, setRol] = useState('colaborador');
  const [cargo, setCargo] = useState('');
  const [personas, setPersonas] = useState<PersonaDelDirectorio[]>([]);
  const [vence, setVence] = useState('');
  const [obligatorio, setObligatorio] = useState(true);
  const [nota, setNota] = useState('');
  const [avisar, setAvisar] = useState(true);
  const [error, setError] = useState('');

  function enviar(e: React.FormEvent) {
    e.preventDefault();
    const base: CuerpoDeAsignacion = {
      target_type: destino,
      due_date: vence || null,
      is_mandatory: obligatorio,
      note: nota.trim() || null,
      notify: avisar,
    };
    let cuerpos: CuerpoDeAsignacion[];
    if (destino === 'area') {
      const id = Number(areaId || areas.data?.[0]?.id);
      if (!id) { setError('Elige un área.'); return; }
      cuerpos = [{ ...base, area_id: id }];
    } else if (destino === 'rol') {
      cuerpos = [{ ...base, target_role: rol }];
    } else if (destino === 'cargo') {
      if (!cargo.trim()) { setError('Escribe el cargo, tal como está en el perfil.'); return; }
      cuerpos = [{ ...base, target_position: cargo.trim() }];
    } else if (destino === 'usuario') {
      if (!personas.length) { setError('Elige al menos a una persona.'); return; }
      cuerpos = personas.map((p) => ({ ...base, user_id: p.id }));
    } else {
      cuerpos = [base];
    }
    setError('');
    crear.mutate(
      { cursoId: curso.id, cuerpos },
      {
        onSuccess: ({ total, hechas, fallo }) => {
          if (fallo) {
            // Solo pasa con varias personas: se quitan de la lista las que ya
            // la tienen y se quedan las que faltan, con la fecha y la nota
            // puestas, para reintentar sin duplicar a nadie.
            const motivo = fallo instanceof Error ? fallo.message : 'error desconocido';
            setPersonas((ps) => ps.filter((_, i) => !hechas.includes(i)));
            Utils.toast(
              `Se asignó a ${hechas.length} de ${cuerpos.length}; con las demás falló (${motivo}). ` +
                'Siguen elegidas para volver a intentarlo.',
              'warning',
              8000,
            );
            return;
          }
          Utils.toast(`Asignación creada: ${total} persona${total === 1 ? '' : 's'}`, 'success');
          setVence(''); setNota(''); setPersonas([]); setCargo('');
        },
        onError: (err) =>
          Utils.toast(err instanceof Error ? err.message : 'No se pudo crear la asignación', 'error'),
      },
    );
  }

  return (
    <form onSubmit={enviar} className="flex flex-col gap-4 rounded-sm border border-line bg-sunken p-4">
      <h3 className="text-3 font-bold text-brand-ink">
        <i className="bi bi-plus-circle" aria-hidden="true" /> Nueva asignación
      </h3>

      {/* Radios nativos: las flechas las pone el navegador, sin código propio,
          y el grupo es una sola parada de Tab. */}
      <fieldset>
        <legend className="mb-1.5 text-2 font-semibold text-ink-2">¿A quién?</legend>
        <div className="flex flex-wrap gap-2">
          {DESTINOS.map(([valor, texto]) => (
            <label
              key={valor}
              className={cn(
                'flex min-h-8 cursor-pointer items-center gap-1.5 rounded-full border px-3 text-2 font-semibold',
                'pointer-coarse:min-h-11 focus-within:focus-anillo',
                destino === valor ? 'border-brand-ink bg-brand text-on-brand' : 'border-line bg-surface text-ink-2',
              )}
            >
              <input
                type="radio"
                name="asg-destino"
                value={valor}
                checked={destino === valor}
                onChange={() => { setDestino(valor); setError(''); }}
                className="sr-only"
              />
              {texto}
            </label>
          ))}
        </div>
      </fieldset>

      {destino === 'area' && (
        <Field label="Área" error={error}>
          {areas.isError ? (
            <EstadoError err={areas.error} que="las áreas" onReintentar={() => void areas.refetch()} />
          ) : (
            <Select value={areaId} onChange={(e) => { setAreaId(e.target.value); setError(''); }} disabled={areas.isLoading}>
              {areas.isLoading && <option>Cargando…</option>}
              {(areas.data ?? []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </Select>
          )}
        </Field>
      )}

      {destino === 'rol' && (
        <Field label="Rol" ayuda="Tres de los roles del sistema; ver la nota de la ficha.">
          <Select value={rol} onChange={(e) => setRol(e.target.value)}>
            {ROLES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </Select>
        </Field>
      )}

      {destino === 'cargo' && (
        <Field label="Cargo" error={error} ayuda="Tiene que coincidir con el cargo del perfil de cada persona.">
          <Input value={cargo} onChange={(e) => { setCargo(e.target.value); setError(''); }} placeholder="Ej: Asesor comercial" />
        </Field>
      )}

      {destino === 'usuario' && (
        <div className="flex flex-col gap-2">
          <span className="text-2 font-semibold text-ink-2">Personas</span>
          <BuscadorDePersonas
            id="asg-personas"
            etiqueta="Buscar a una persona para asignarle el curso"
            marcador="Escribe un nombre…"
            excluir={personas.map((p) => p.id)}
            alElegir={(p) => { setPersonas((ps) => [...ps, p]); setError(''); }}
          />
          {personas.length > 0 ? (
            <ul aria-label="Personas elegidas" className="flex flex-wrap gap-1.5">
              {personas.map((p) => (
                <li key={p.id} className="flex items-center gap-1 rounded-full bg-brand py-1 pl-3 pr-1 text-2 text-on-brand">
                  {p.full_name}
                  <button
                    type="button"
                    aria-label={`Quitar a ${p.full_name}`}
                    onClick={() => setPersonas((ps) => ps.filter((x) => x.id !== p.id))}
                    className="flex size-6 items-center justify-center rounded-full text-inherit hover:bg-white/15 focus-visible:focus-anillo"
                  >
                    <i className="bi bi-x-lg" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className={cn('text-2', error ? 'text-danger-ink' : 'text-muted')}>
              {error || 'Todavía no has elegido a nadie.'}
            </p>
          )}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Fecha límite" opcional>
          <Input type="date" value={vence} onChange={(e) => setVence(e.target.value)} />
        </Field>
        <label className="flex min-h-11 cursor-pointer items-center gap-2 self-end pb-2 text-3 text-ink-2">
          <input
            type="checkbox"
            checked={obligatorio}
            onChange={(e) => setObligatorio(e.target.checked)}
            className="size-4 accent-brand"
          />
          Obligatorio
        </label>
      </div>

      <Field label="Nota" opcional>
        <Input value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Ej: Capacitación trimestral de seguridad" />
      </Field>

      <label className="flex min-h-11 cursor-pointer items-center gap-2 text-3 text-ink-2">
        <input type="checkbox" checked={avisar} onChange={(e) => setAvisar(e.target.checked)} className="size-4 accent-brand" />
        Avisar por correo a quien la recibe
      </label>

      <div>
        <Button type="submit" loading={crear.isPending}>
          <i className="bi bi-check2-circle" aria-hidden="true" /> Crear asignación
        </Button>
      </div>
    </form>
  );
}

/* ── Progreso del equipo ───────────────────────────────────────────────── */

const ESTADOS: Record<string, { texto: string; clase: string }> = {
  completado: { texto: 'Completado', clase: 'text-ok-ink' },
  vencido: { texto: 'Vencido', clase: 'text-danger-ink' },
  proximo: { texto: 'Por vencer', clase: 'text-warn-ink' },
  al_dia: { texto: 'Al día', clase: 'text-muted' },
};

function ProgresoDelEquipo({ curso, onVolver }: { curso: CursoDetalle; onVolver: () => void }) {
  const equipo = useProgresoDelEquipo(curso.id, true);
  const reiniciar = useReiniciarProgreso();
  const [reiniciando, setReiniciando] = useState<{ id: number; nombre: string } | null>(null);

  const s = equipo.data?.summary;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Button variant="ghost" size="sm" onClick={onVolver}>
          <i className="bi bi-arrow-left" aria-hidden="true" /> Volver a las asignaciones
        </Button>
      </div>

      {equipo.isLoading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      ) : equipo.isError ? (
        <EstadoError err={equipo.error} que="el progreso del equipo" onReintentar={() => void equipo.refetch()} onVolver={onVolver} />
      ) : !s ? null : (
        <>
          <ul className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            {([
              ['Total', s.total, 'text-brand-ink'],
              ['Completados', s.completados, 'text-ok-ink'],
              ['En progreso', s.en_progreso, 'text-info-ink'],
              ['Sin iniciar', s.sin_iniciar, 'text-muted'],
              ['Vencidos', s.vencidos, 'text-danger-ink'],
            ] as const).map(([t, n, clase]) => (
              <li key={t} className="rounded-sm border border-line bg-surface p-2.5 text-center">
                <span className={cn('block text-6 font-extrabold leading-none tabular-nums', clase)}>{n}</span>
                <span className="mt-1 block text-1 text-muted">{t}</span>
              </li>
            ))}
          </ul>

          {!equipo.data!.rows.length ? (
            <p className="rounded-sm border border-line bg-sunken p-3 text-3 text-ink-2">
              Todavía no hay a quién seguir: crea una asignación primero.
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {equipo.data!.rows.map((r) => {
                const est = ESTADOS[r.status] ?? ESTADOS.al_dia!;
                return (
                  <li key={r.user_id} className="flex flex-wrap items-center gap-3 rounded-sm border border-line bg-sunken p-2.5">
                    <div className="min-w-40 flex-1">
                      <p className="truncate text-3 font-semibold text-ink">{r.full_name}</p>
                      <p className="truncate text-1 text-muted">
                        {r.area}
                        {r.position ? ` · ${r.position}` : ''}
                        {r.due_date ? ` · vence ${Utils.fmtDateOnly(r.due_date)}` : ''}
                      </p>
                    </div>
                    <div className="w-24">
                      <BarraDeProgreso pct={r.progress_pct} etiqueta={`Avance de ${r.full_name}`} />
                    </div>
                    <span className={cn('w-20 text-right text-1 font-semibold', est.clase)}>{est.texto}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Reiniciar el progreso de ${r.full_name}`}
                      onClick={() => setReiniciando({ id: r.user_id, nombre: r.full_name })}
                    >
                      <i className="bi bi-arrow-counterclockwise" aria-hidden="true" />
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}

      <ConfirmDialog
        abierto={reiniciando != null}
        onCambio={(v) => { if (!v) setReiniciando(null); }}
        titulo="Reiniciar el progreso"
        descripcion={
          reiniciando
            ? `${reiniciando.nombre} pierde su avance en este curso y tendrá que hacerlo de nuevo.`
            : ''
        }
        textoConfirmar="Reiniciar"
        destructivo
        cargando={reiniciar.isPending}
        onConfirmar={() => {
          if (!reiniciando) return;
          reiniciar.mutate(
            { cursoId: curso.id, userId: reiniciando.id },
            {
              onSuccess: () => { setReiniciando(null); Utils.toast('Progreso reiniciado', 'info'); },
              onError: (err) => {
                setReiniciando(null);
                Utils.toast(
                  // El resto del panel exige `cursos:manage`; este endpoint es
                  // `AdminOnly` (ver la nota de arriba). El vanilla enseñaba el
                  // «No tienes permiso» del servidor sin decir de quién es.
                  estadoDe(err) === 403
                    ? 'Reiniciar el progreso es solo de administración: pídeselo a un administrador.'
                    : err instanceof Error ? err.message : 'No se pudo reiniciar',
                  'error',
                );
              },
            },
          );
        }}
      />
    </div>
  );
}
